import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, Lock, ShieldCheck } from "lucide-react";
import { BkaWordmark } from "@/components/BkaLogo";
import { Seo } from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { AdminBanner, AdminField, adminInputClass } from "@/components/admin/AdminUi";

export default function AdminLogin() {
  const { user, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user && user !== true) return <Navigate to={location.state?.from || "/admin"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await signIn(email, password);
    setBusy(false);
    if (res.ok) navigate(location.state?.from || "/admin", { replace: true });
    else setError(res.error);
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Seo title="Sign In | Bahamas Kidney Association CMS" noindex />

      <div className="relative hidden flex-1 overflow-hidden bg-bka-navy lg:block">
        <div className="bka-grain absolute inset-0" aria-hidden="true" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <BkaWordmark tone="dark" compact />
          <div>
            <h1 className="max-w-md font-display text-4xl font-bold leading-[1.1] text-white">
              Content Management System
            </h1>
            <p className="mt-5 max-w-sm text-base text-white/70">
              Manage pages, programs, blog posts, testimonials, enquiries and Emerald Honours nominations —
              all in one place.
            </p>
            <p className="bka-script mt-8 text-3xl text-bka-gold">A Healthier Bahamas.</p>
          </div>
          <div className="flex items-center gap-2 text-xs font-semibold text-white/50">
            <ShieldCheck className="h-4 w-4" />
            Staging environment · access is audited
          </div>
        </div>
      </div>

      <div className="flex w-full items-center justify-center p-6 lg:w-[520px] lg:p-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <BkaWordmark compact />
          </div>
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-bka-aqua text-bka-emerald">
            <Lock className="h-5 w-5" />
          </span>
          <h2 className="mt-5 font-display text-2xl font-bold text-bka-navy">Sign in to the CMS</h2>
          <p className="mt-2 text-sm text-slate-500">Enter your credentials to continue.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form" noValidate>
            <AdminBanner status="error" message={error} testId="admin-login-error" />

            <AdminField label="Email Address" htmlFor="login-email" required>
              <input
                id="login-email"
                data-testid="admin-login-email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={adminInputClass}
                placeholder="you@example.com"
              />
            </AdminField>

            <AdminField label="Password" htmlFor="login-password" required>
              <div className="relative">
                <input
                  id="login-password"
                  data-testid="admin-login-password"
                  type={show ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${adminInputClass} pr-11`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  data-testid="admin-login-toggle-password"
                  onClick={() => setShow((v) => !v)}
                  aria-label={show ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-bka-navy"
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </AdminField>

            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={busy}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-bka-emerald px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-bka-emeraldDark disabled:opacity-60"
            >
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Signing in…
                </>
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          <p className="mt-6 text-xs leading-relaxed text-slate-500">
            For security, repeated failed sign-in attempts temporarily lock the account. Contact a Super Admin
            if you need your password reset.
          </p>
        </div>
      </div>
    </div>
  );
}
